import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../client'
import { packDraftPayload } from '../transforms'
import { toast } from 'sonner'
import type { BlogDraft, UpdateDraftPayload, ImageCandidatesResponse } from '../types'

export function useApproveDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload?: { scheduledAt?: string }) =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/approve`,
        {
          method: 'POST',
          body: payload ? JSON.stringify(payload) : undefined,
        },
      ),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      const previous = queryClient.getQueryData<BlogDraft>(['drafts', 'detail', agentId, draftId])
      if (previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], {
          ...previous,
          status: payload?.scheduledAt ? 'scheduled' : 'approved',
          scheduledAt: payload?.scheduledAt ?? null,
        } as BlogDraft)
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], context.previous)
      }
      toast.error('Failed to approve draft. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
    },
    onSuccess: (_data, variables) => {
      toast.success(variables?.scheduledAt ? 'Draft approved and publish scheduled' : 'Draft approved')
    },
  })
}

export function useRejectDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { reason: string }) =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/reject`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      const previous = queryClient.getQueryData<BlogDraft>(['drafts', 'detail', agentId, draftId])
      if (previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], {
          ...previous,
          status: 'rejected',
          rejectionReason: payload.reason,
        } as BlogDraft)
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], context.previous)
      }
      toast.error('Failed to reject draft. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
    },
    onSuccess: () => {
      toast.success('Draft rejected')
    },
  })
}

export function useRedraftDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/redraft`,
        { method: 'POST' },
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      const previous = queryClient.getQueryData<BlogDraft>(['drafts', 'detail', agentId, draftId])
      if (previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], {
          ...previous,
          status: 'draft',
          rejectionReason: null,
        } as BlogDraft)
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], context.previous)
      }
      toast.error('Failed to return to draft. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
    },
    onSuccess: () => {
      toast.success('Draft returned to editing')
    },
  })
}

export function useUpdateDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateDraftPayload) =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}`,
        { method: 'PATCH', body: JSON.stringify(packDraftPayload(payload)) },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
    },
    onSuccess: () => {
      toast.success('Draft saved')
    },
    onError: () => {
      toast.error('Failed to save draft. Please try again.')
    },
  })
}

export function useSchedulePublish(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { scheduledAt: string }) =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/schedule`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      const previous = queryClient.getQueryData<BlogDraft>(['drafts', 'detail', agentId, draftId])
      if (previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], {
          ...previous,
          status: 'scheduled',
          scheduledAt: payload.scheduledAt,
        } as BlogDraft)
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], context.previous)
      }
      toast.error('Failed to schedule publish. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
    },
    onSuccess: () => {
      toast.success('Publish scheduled')
    },
  })
}

export function useGenerateImage(agentId: string, draftId: string) {
  return useMutation({
    mutationFn: (payload: { prompt: string }) =>
      apiClient<{ imageUrl: string }>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/images/generate`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onError: () => {
      toast.error('Image generation failed. Try a different prompt.')
    },
  })
}

export function useSelectCandidate(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (candidateId: string) =>
      apiClient<{ candidateId: string; selected: boolean }>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/candidates/${candidateId}/select`,
        { method: 'POST' },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'candidates', agentId, draftId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
    },
    onSuccess: () => {
      toast.success('Image selected')
    },
    onError: () => {
      toast.error('Failed to select image')
    },
  })
}

export function useRepublishDraft(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiClient<BlogDraft>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/republish`,
        { method: 'POST' },
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
      const previous = queryClient.getQueryData<BlogDraft>(['drafts', 'detail', agentId, draftId])
      if (previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], {
          ...previous,
          publishStatus: 'pending',
          publishError: null,
        } as BlogDraft)
      }
      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drafts', 'detail', agentId, draftId], context.previous)
      }
      toast.error('Failed to republish. Please try again.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'list', agentId] })
      queryClient.invalidateQueries({ queryKey: ['drafts', 'detail', agentId, draftId] })
    },
    onSuccess: () => {
      toast.success('Republish triggered')
    },
  })
}

export function useRegenerateCandidates(agentId: string, draftId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: { prompt: string }) =>
      apiClient<ImageCandidatesResponse>(
        `/api/v1/agents/${agentId}/drafts/${draftId}/candidates/regenerate`,
        { method: 'POST', body: JSON.stringify(payload) },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['drafts', 'candidates', agentId, draftId] })
    },
    onSuccess: () => {
      toast.success('New images generated')
    },
    onError: () => {
      toast.error('Image generation failed. Try a different prompt.')
    },
  })
}
